import LanguageSelector from '../i18n/LanguageSelector';
import { useAppTranslation } from '../i18n/useAppTranslation';
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container, TextField, Button, Typography, Box, Alert, Paper, InputAdornment, IconButton
} from '@mui/material';
import { Lock, ArrowBack, SportsScore, Visibility, VisibilityOff } from '@mui/icons-material';
import { publicApi as api } from '../services/api';

const ResetPassword = () => {
  const { tr } = useAppTranslation();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setError('Lien invalide ou expiré. Veuillez refaire une demande.');
    }
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }

    setLoading(true);
    try {
      await api.post('/users/reset-password/', { token, new_password: newPassword });
      setSuccess(true);
    } catch (err: any) {
      if (err.code === 'ERR_NETWORK') {
        setError('Impossible de contacter le serveur.');
      } else {
        setError(err.response?.data?.error || 'Lien invalide ou expiré.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #0f172a 100%)' }}>
      <Container maxWidth="xs">
        <Paper elevation={24} sx={{ p: 4, borderRadius: 4, bgcolor: 'background.paper' }}>
<LanguageSelector/>

          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <SportsScore sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
            <Typography variant="h5" fontWeight={800} gutterBottom>{tr("Nouveau mot de passe")}</Typography>
            <Typography variant="body2" color="text.secondary">
               {tr("Choisis un nouveau mot de passe sécurisé pour ton compte.")} </Typography>
          </Box>

          {success ? (
            <Box sx={{ textAlign: 'center' }}>
              <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
                 {tr("Ton mot de passe a été réinitialisé avec succès !")} </Alert>
              <Button fullWidth variant="contained" onClick={() => navigate('/login')}
                sx={{ borderRadius: 2, py: 1.5, fontWeight: 700 }}>
                 {tr("Se connecter")} </Button>
            </Box>
          ) : (
            <Box component="form" onSubmit={handleSubmit}>
              {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{typeof error === 'string' ? tr(error) : error}</Alert>}

              <TextField
                margin="normal"
                required
                fullWidth
                label={tr("Nouveau mot de passe")}
                type={showPassword ? 'text' : 'password'}
                autoFocus
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                variant="outlined"
                helperText={tr("Minimum 8 caractères")}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start"><Lock color="action" /></InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />

              <TextField
                margin="normal"
                required
                fullWidth
                label={tr("Confirmer le mot de passe")}
                type={showConfirm ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                variant="outlined"
                error={confirmPassword.length > 0 && newPassword !== confirmPassword}
                helperText={confirmPassword.length > 0 && newPassword !== confirmPassword ? tr("Les mots de passe ne correspondent pas") : ''}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start"><Lock color="action" /></InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowConfirm(!showConfirm)} edge="end">
                        {showConfirm ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading || !token}
                sx={{ mt: 3, mb: 2, py: 1.5, borderRadius: 2, fontWeight: 700 }}
              >
                {loading ? tr("Réinitialisation...") : tr("Réinitialiser le mot de passe")}
              </Button>
              <Button
                fullWidth
                variant="text"
                startIcon={<ArrowBack />}
                onClick={() => navigate('/login')}
                sx={{ borderRadius: 2 }}
              >
                 {tr("Retour à la connexion")} </Button>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default ResetPassword;
